import * as React from 'react';
import lottie, { AnimationConfigWithData, AnimationItem } from 'lottie-web';

const { useRef, useEffect, forwardRef, useImperativeHandle } = React;

export type LottieAnimationConfig = Pick<AnimationConfigWithData, 'autoplay' | 'loop'>;

export interface LottieAnimationProps extends LottieAnimationConfig {
    animationData: any;
    className?: string;
    title?: string;
    handlers?: React.DOMAttributes<HTMLElement>;
}

export interface LottieAnimationRef
    extends Pick<AnimationItem, 'play' | 'stop' | 'pause' | 'goToAndPlay' | 'goToAndStop'> {}

function LottieAnimationInternal(props: LottieAnimationProps, ref: any) {
    const elem = useRef<HTMLDivElement | null>(null);
    const animation = useRef<AnimationItem | null>(null);
    const autoplay = props.autoplay === undefined ? true : props.autoplay;
    const { className, animationData, loop, title } = props;

    useEffect(() => {
        animation.current = lottie.loadAnimation({
            autoplay,
            loop,
            animationData,
            container: elem.current as Element,
        });

        return () => animation.current!.destroy();
    }, [elem, animationData, autoplay, loop]);

    useImperativeHandle(ref, () => ({
        play: () => animation.current?.play(),
        stop: () => animation.current?.stop(),
        pause: () => animation.current?.pause(),
        goToAndPlay: (value: number) => animation.current?.goToAndPlay(value, true),
        goToAndStop: (value: number) => animation.current?.goToAndStop(value, true),
    }));

    return (
        <div
            aria-label={title}
            title={title}
            data-is-focusable={true}
            className={className}
            ref={elem}
            {...props.handlers}
        />
    );
}

export const LottieAnimation = forwardRef<LottieAnimationRef, LottieAnimationProps>(
    LottieAnimationInternal
);
